// GPU CBT — compute-shader tessellation alternative to CPU cbt.ts.
// Activated via ?gpu-cbt URL parameter. All tessellation runs on GPU.

export interface GpuCbtTileState {
  bitfield: GPUBuffer;      // 4KB, STORAGE|COPY_DST
  leafList: GPUBuffer;      // 64KB, STORAGE
  leafCount: GPUBuffer;     // 16B, STORAGE|COPY_DST
  flags: GPUBuffer;         // 64KB, STORAGE
  vertexBuf: GPUBuffer;     // 1.2MB, STORAGE|VERTEX
  indirectBuf: GPUBuffer;   // 16B, STORAGE|INDIRECT
  paramsBuffer: GPUBuffer;  // 48B, UNIFORM|COPY_DST
  bindGroup: GPUBindGroup;
}

// CbtParams layout (48 bytes, std140):
// cam_pos: vec3<f32>      (offset 0, 12 bytes)
// screen_h: f32           (offset 12, 4 bytes)
// elev_scale: f32         (offset 16, 4 bytes)
// elev_range: f32         (offset 20, 4 bytes)
// lod_threshold: f32      (offset 24, 4 bytes)
// max_depth: u32          (offset 28, 4 bytes)
// tile_offset: vec2<f32>  (offset 32, 8 bytes)
// _pad: vec2<f32>         (offset 40, 8 bytes)
// Total: 48 bytes
const PARAMS_SIZE = 48;

const GPU_CBT_MAX_DEPTH = 14;
const MAX_LEAVES = 16384; // 2^14
// Max verts: each leaf produces 3 surface + up to 18 skirt = 21 verts max
// Budget: 16384 * 21 = ~344K verts * 20 bytes = ~6.5MB. Use 8MB for safety.
const MAX_VERTEX_FLOATS = 8 * 1024 * 1024 / 4; // 2M floats = 8MB
const BITFIELD_WORDS = 1024; // 32KB / 32 = 1024 u32 words = covers keys 0..32767
const LEAF_LIST_SIZE = MAX_LEAVES * 4; // 64KB
const FLAGS_SIZE = MAX_LEAVES * 4; // 64KB

const SPLIT_ITERATIONS = 4;

// Reusable params buffer for writeBuffer
const _paramsBuf = new ArrayBuffer(PARAMS_SIZE);
const _paramsF32 = new Float32Array(_paramsBuf);
const _paramsU32 = new Uint32Array(_paramsBuf);

export class GpuCbt {
  private classifyPipeline: GPUComputePipeline;
  private splitPipeline: GPUComputePipeline;
  private mergePipeline: GPUComputePipeline;
  private rebuildPipeline: GPUComputePipeline;
  private meshPipeline: GPUComputePipeline;
  private indirectPipeline: GPUComputePipeline;
  private bindGroupLayout: GPUBindGroupLayout;
  private device: GPUDevice;

  constructor(device: GPUDevice, shaderCode: string) {
    this.device = device;

    const module = device.createShaderModule({ code: shaderCode });

    this.bindGroupLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 5, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 6, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        { binding: 7, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: 'float', viewDimension: '2d' } },
        { binding: 8, visibility: GPUShaderStage.COMPUTE, sampler: { type: 'filtering' } },
      ],
    });

    const pipelineLayout = device.createPipelineLayout({
      bindGroupLayouts: [this.bindGroupLayout],
    });

    const makePipeline = (entryPoint: string) => device.createComputePipeline({
      layout: pipelineLayout,
      compute: { module, entryPoint },
    });

    this.classifyPipeline = makePipeline('classify');
    this.splitPipeline = makePipeline('apply_splits');
    this.mergePipeline = makePipeline('apply_merges');
    this.rebuildPipeline = makePipeline('rebuild_leaf_list');
    this.meshPipeline = makePipeline('generate_mesh');
    this.indirectPipeline = makePipeline('write_indirect');
  }

  createTileState(
    device: GPUDevice,
    heightmapView: GPUTextureView,
    sampler: GPUSampler,
  ): GpuCbtTileState {
    const bitfield = device.createBuffer({
      size: BITFIELD_WORDS * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });

    const leafList = device.createBuffer({
      size: LEAF_LIST_SIZE,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });

    const leafCount = device.createBuffer({
      size: 16, // 4 atomic u32s
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });

    const flags = device.createBuffer({
      size: FLAGS_SIZE,
      usage: GPUBufferUsage.STORAGE,
    });

    const vertexBuf = device.createBuffer({
      size: MAX_VERTEX_FLOATS * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.VERTEX,
    });

    const indirectBuf = device.createBuffer({
      size: 16,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.INDIRECT,
    });

    const paramsBuffer = device.createBuffer({
      size: PARAMS_SIZE,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    const bindGroup = device.createBindGroup({
      layout: this.bindGroupLayout,
      entries: [
        { binding: 0, resource: { buffer: bitfield } },
        { binding: 1, resource: { buffer: leafList } },
        { binding: 2, resource: { buffer: leafCount } },
        { binding: 3, resource: { buffer: flags } },
        { binding: 4, resource: { buffer: vertexBuf } },
        { binding: 5, resource: { buffer: indirectBuf } },
        { binding: 6, resource: { buffer: paramsBuffer } },
        { binding: 7, resource: heightmapView },
        { binding: 8, resource: sampler },
      ],
    });

    // Initialize bitfield with two root leaves (keys 2 and 3)
    const initBitfield = new Uint32Array(BITFIELD_WORDS);
    initBitfield[0] = (1 << 2) | (1 << 3); // bits 2 and 3 set
    device.queue.writeBuffer(bitfield, 0, initBitfield);

    // Initialize leaf_list with [2, 3] and leaf_count[0] = 2
    const initLeafList = new Uint32Array([2, 3]);
    device.queue.writeBuffer(leafList, 0, initLeafList);
    const initLeafCount = new Uint32Array([2, 0, 0, 0]);
    device.queue.writeBuffer(leafCount, 0, initLeafCount);

    return {
      bitfield,
      leafList,
      leafCount,
      flags,
      vertexBuf,
      indirectBuf,
      paramsBuffer,
      bindGroup,
    };
  }

  encodeTileUpdate(
    encoder: GPUCommandEncoder,
    tile: GpuCbtTileState,
    camPosLocalX: number, camPosLocalY: number, camPosLocalZ: number,
    screenH: number, elevScale: number, elevRange: number,
    lodThreshold: number,
    tileOffsetX: number, tileOffsetZ: number,
  ): void {
    const queue = this.device.queue;

    // Write params uniform
    _paramsF32[0] = camPosLocalX;
    _paramsF32[1] = camPosLocalY;
    _paramsF32[2] = camPosLocalZ;
    _paramsF32[3] = screenH;
    _paramsF32[4] = elevScale;
    _paramsF32[5] = elevRange;
    _paramsF32[6] = lodThreshold;
    _paramsU32[7] = GPU_CBT_MAX_DEPTH;
    _paramsF32[8] = tileOffsetX;
    _paramsF32[9] = tileOffsetZ;
    _paramsF32[10] = 0; // pad
    _paramsF32[11] = 0; // pad
    queue.writeBuffer(tile.paramsBuffer, 0, _paramsBuf, 0, PARAMS_SIZE);

    const leafWorkgroups = Math.ceil(MAX_LEAVES / 64);

    // Pass 1: classify
    {
      const pass = encoder.beginComputePass();
      pass.setPipeline(this.classifyPipeline);
      pass.setBindGroup(0, tile.bindGroup);
      pass.dispatchWorkgroups(leafWorkgroups);
      pass.end();
    }

    // Pass 2: apply_splits × SPLIT_ITERATIONS
    for (let i = 0; i < SPLIT_ITERATIONS; i++) {
      const pass = encoder.beginComputePass();
      pass.setPipeline(this.splitPipeline);
      pass.setBindGroup(0, tile.bindGroup);
      pass.dispatchWorkgroups(leafWorkgroups);
      pass.end();
    }

    // Pass 3: apply_merges
    {
      const pass = encoder.beginComputePass();
      pass.setPipeline(this.mergePipeline);
      pass.setBindGroup(0, tile.bindGroup);
      pass.dispatchWorkgroups(leafWorkgroups);
      pass.end();
    }

    // Pass 4: rebuild_leaf_list (zero leaf_count[0] first)
    encoder.clearBuffer(tile.leafCount, 0, 4);
    {
      const pass = encoder.beginComputePass();
      pass.setPipeline(this.rebuildPipeline);
      pass.setBindGroup(0, tile.bindGroup);
      pass.dispatchWorkgroups(Math.ceil(BITFIELD_WORDS / 64));
      pass.end();
    }

    // Pass 5: generate_mesh (zero leaf_count[2] first)
    encoder.clearBuffer(tile.leafCount, 8, 4);
    {
      const pass = encoder.beginComputePass();
      pass.setPipeline(this.meshPipeline);
      pass.setBindGroup(0, tile.bindGroup);
      pass.dispatchWorkgroups(leafWorkgroups);
      pass.end();
    }

    // Pass 6: write_indirect
    {
      const pass = encoder.beginComputePass();
      pass.setPipeline(this.indirectPipeline);
      pass.setBindGroup(0, tile.bindGroup);
      pass.dispatchWorkgroups(1);
      pass.end();
    }
  }

  destroyTileState(state: GpuCbtTileState): void {
    state.bitfield.destroy();
    state.leafList.destroy();
    state.leafCount.destroy();
    state.flags.destroy();
    state.vertexBuf.destroy();
    state.indirectBuf.destroy();
    state.paramsBuffer.destroy();
  }
}
